import React, { useEffect, useState } from "react";
import { ref, onValue, remove, set, get } from "firebase/database";
import { database, firestore } from "../../../firebaseConfig";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { Clock, CheckCircle, XCircle, User } from "lucide-react";

const updateClientRequestStatus = (
  uid,
  company,
  requestedAt,
  status,
  decidedAt,
  extraFields = {}
) =>
  set(ref(database, `ClientCompanyRequests/${uid}/${company}`), {
    companyName: company,
    requestedAt,
    status,
    decidedAt,
    ...extraFields,
  });

const upsertPendingRequests = (
  pendingRequests,
  company,
  status,
  decidedAt,
  requestedAt,
  hasPendingEntry,
  hasRequestInfo
) => {
  if (hasPendingEntry) {
    return pendingRequests.map((req) =>
      req.companyName === company ? { ...req, status, decidedAt } : req
    );
  }

  if (hasRequestInfo) {
    // RTDB might have the request even if Firestore array is missing.
    // Add the entry so the client can still see the decision status.
    return [
      ...pendingRequests,
      {
        companyName: company,
        requestedAt,
        status,
        decidedAt,
      },
    ];
  }

  return pendingRequests;
};

const extractPendingInfo = (userDoc, company) => {
  if (!userDoc?.exists()) {
    return {
      userData: null,
      pendingRequests: [],
      matchingPending: null,
      hasPendingEntry: false,
    };
  }

  const userData = userDoc.data();
  const pendingRequests = userData.pendingCompanyRequests || [];
  const matchingPending =
    pendingRequests.find((req) => req.companyName === company) || null;

  return {
    userData,
    pendingRequests,
    matchingPending,
    hasPendingEntry: Boolean(matchingPending),
  };
};

const PendingClientRequests = ({ companyName }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);

  // Load pending client requests
  useEffect(() => {
    if (!companyName) return;

    const reqRef = ref(database, `Companies/${companyName}/ClientRequests`);

    const unsub = onValue(reqRef, (snap) => {
      const data = snap.val() || {};
      const requestsList = Object.entries(data).map(([uid, req]) => ({
        uid,
        ...req,
      }));
      setRequests(requestsList);
      setLoading(false);
    });

    return () => unsub();
  }, [companyName]);

  // Fetch validated client snapshots for the company
  const fetchSnapshots = async () => {
    if (!companyName) return;

    const clientsRef = ref(
      database,
      `Companies/${companyName}/ValidatedClients`
    );

    const snap = await get(clientsRef);

    const arr = [];
    if (snap.exists()) {
      const data = snap.val();

      Object.entries(data).forEach(([id, c]) => {
        // Only show clients that are not yet linked to any user
        if (!c.LinkedClients || c.LinkedClients.length === 0) {
          arr.push({ id, ...c });
        }
      });
    }

    setSnapshots(arr);
  };

  // Open snapshot selector modal
  const handleAttach = async (req) => {
    setSelectedUid(req.uid);
    await fetchSnapshots();
  };

  // Confirm attach - link user UID to selected client snapshot
  const confirmAttach = async (uid, snapshotId) => {
    if (!snapshotId) {
      toast.error("Please select a client profile to attach");
      return;
    }

    try {
      const now = new Date().toISOString();
      const requestInfo = requests.find((r) => r.uid === uid);

      // 1️⃣ Get existing LinkedClients array or create new one
      const clientRef = ref(
        database,
        `Companies/${companyName}/ValidatedClients/${snapshotId}/LinkedClients`
      );
      const linkedSnap = await get(clientRef);
      const linkedClients = linkedSnap.exists() ? linkedSnap.val() : [];

      // Add UID if not already linked
      if (!linkedClients.includes(uid)) {
        linkedClients.push(uid);
        
        // Update ValidatedClients with new LinkedClients array
        await set(clientRef, linkedClients);
        
        // Also update AIValidatedClients for consistency
        await set(
          ref(
            database,
            `Companies/${companyName}/AIValidatedClients/${snapshotId}/LinkedClients`
          ),
          linkedClients
        );
      }

      // 2️⃣ Update Firestore user - add to connectedCompanies array
      const userRef = doc(firestore, "users", uid);
      const userDoc = await getDoc(userRef);
      
      const {
        userData,
        pendingRequests,
        matchingPending,
        hasPendingEntry,
      } = extractPendingInfo(userDoc, companyName);
      const finalRequestTimestamp =
        requestInfo?.requestedAt || matchingPending?.requestedAt || null;

      if (userData) {
        const connectedCompanies = userData.connectedCompanies || [];
        const updatedPending = upsertPendingRequests(
          pendingRequests,
          companyName,
          "approved",
          now,
          finalRequestTimestamp,
          hasPendingEntry,
          Boolean(requestInfo)
        );
        const updatePayload = {
          pendingCompanyRequests: updatedPending,
          [`clientSnapshots.${companyName}`]: snapshotId, // Track which snapshot per company
        };
        
        // Add company if not already in the list (clients can connect to multiple companies)
        if (!connectedCompanies.includes(companyName)) {
          updatePayload.connectedCompanies = [
            ...connectedCompanies,
            companyName,
          ];
        }

        await updateDoc(userRef, updatePayload);
      }

      // 3️⃣ Remove the request from RTDB
      await remove(
        ref(database, `Companies/${companyName}/ClientRequests/${uid}`)
      );

      // 4️⃣ Reflect status on client's request node
      await updateClientRequestStatus(
        uid,
        companyName,
        finalRequestTimestamp,
        "approved",
        now,
        { snapshotId }
      );

      toast.success("Client successfully attached!");

      setSelectedUid(null);
      setSnapshots([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to attach client");
    }
  };

  // Decline client request
  const handleDecline = async (uid, company) => {
    try {
      const now = new Date().toISOString();
      const requestInfo = requests.find((r) => r.uid === uid);
      const userRef = doc(firestore, "users", uid);
      const userDoc = await getDoc(userRef);
      const {
        userData,
        pendingRequests,
        matchingPending,
        hasPendingEntry,
      } = extractPendingInfo(userDoc, company);
      const requestedAtValue =
        requestInfo?.requestedAt || matchingPending?.requestedAt || null;

      // Remove request from RTDB
      await remove(
        ref(database, `Companies/${company}/ClientRequests/${uid}`)
      );

      // Update client-side request status in RTDB
      await updateClientRequestStatus(
        uid,
        company,
        requestedAtValue,
        "declined",
        now
      );

      // Update Firestore - mark as declined
      if (userData) {
        const updatedPending = upsertPendingRequests(
          pendingRequests,
          company,
          "declined",
          now,
          requestedAtValue,
          hasPendingEntry,
          Boolean(requestInfo)
        );

        await updateDoc(userRef, {
          pendingCompanyRequests: updatedPending,
        });
      }

      toast.success("Request declined");
    } catch (error) {
      console.error("Error declining request:", error);
      toast.error("Failed to decline request");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-gray-600">Loading requests...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-cyan-700 mb-4 flex items-center gap-2">
        <Clock size={24} />
        Pending Client Requests
      </h2>

      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No pending client requests</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {requests.map((request) => (
            <div
              key={request.uid}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                    <User className="text-cyan-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {request.clientName || request.fullName}
                    </h3>
                    <p className="text-gray-600 text-sm">{request.email}</p>
                    {request.phoneNumber && (
                      <p className="text-gray-500 text-sm">{request.phoneNumber}</p>
                    )}
                    {request.location && (
                      <p className="text-gray-500 text-sm">{request.location}</p>
                    )}
                    {request.description && (
                      <div className="mt-2 p-2 bg-cyan-50 rounded border-l-4 border-cyan-500">
                        <p className="text-xs font-semibold text-cyan-700 mb-1">Client Info:</p>
                        <p className="text-sm text-gray-700">{request.description}</p>
                      </div>
                    )}
                    <p className="text-gray-400 text-xs mt-2">
                      Requested: {new Date(request.requestedAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDecline(request.uid, companyName)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
                  >
                    <XCircle size={16} />
                    Decline
                  </button>
                  <button
                    onClick={() => handleAttach(request)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <CheckCircle size={16} />
                    Attach
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SNAPSHOT SELECT MODAL - Similar to Farmer System */}
      {selectedUid && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-3 text-cyan-700">
              Select Client Snapshot
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Link this user to an existing client record in your system. 
              Only unlinked client snapshots are shown.
            </p>

            {snapshots.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">No client snapshots available</p>
                <p className="text-sm text-gray-400 mt-2">
                  Create a client record first in the Clients tab
                </p>
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {snapshots.map((snap) => (
                  <button
                    key={snap.id}
                    onClick={() => confirmAttach(selectedUid, snap.id)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition text-left"
                  >
                    <div className="font-medium text-gray-800">
                      {snap.CloudName || snap.OtherName}
                    </div>
                    <div className="text-sm text-gray-600">
                      Username: {snap.OtherName}
                    </div>
                    {snap.PhoneNumber && (
                      <div className="text-xs text-gray-500">
                        Phone: {snap.PhoneNumber}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            <button
              className="w-full py-2 bg-gray-300 hover:bg-gray-400 rounded-lg transition"
              onClick={() => setSelectedUid(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingClientRequests;
